'use client';

/**
 * useSoldOut - Unified "Sold Out" hook
 *
 * Reads `settings/soldOut` from Firestore (via lib/admin/sold-out-service.ts,
 * which owns the module-level cache) to determine which listings (lodges,
 * units, etc.) are sold out on the public website.
 *
 * Usage:
 *   const { isSoldOut } = useSoldOut()
 *   isSoldOut(lodge.id, staticSoldOut)
 *
 * - While Firestore loads: falls back to the static `staticSoldOut` flag
 * - After load: uses Firestore as the single source of truth (can also
 *   DESmark a statically sold-out item)
 * - Items not in Firestore: fall back to the static flag
 *
 * Same pattern as hooks/useActivityVisibility.ts, except the cache lives in
 * lib/admin/sold-out-service.ts so the admin write path (setItemSoldOut) can
 * invalidate it without a circular import.
 */

import { useEffect, useState } from 'react';
import { fetchSoldOutMap, getCachedSoldOutMap, SoldOutMap } from '@/lib/admin/sold-out-service';

interface UseSoldOutReturn {
  /** Whether an item should be shown as sold out. Pass the static flag as fallback. */
  isSoldOut: (id: string, staticSoldOut?: boolean) => boolean;
  /** Whether Firestore data has loaded */
  loaded: boolean;
}

export function useSoldOut(): UseSoldOutReturn {
  const [map, setMap] = useState<SoldOutMap | null>(getCachedSoldOutMap());
  const [loaded, setLoaded] = useState(getCachedSoldOutMap() !== null);

  useEffect(() => {
    // Already cached from a previous render/component
    const cached = getCachedSoldOutMap();
    if (cached !== null) {
      setMap(cached);
      setLoaded(true);
      return;
    }

    let cancelled = false;

    fetchSoldOutMap().then((data) => {
      if (!cancelled) {
        setMap(data);
        setLoaded(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const isSoldOut = (id: string, staticSoldOut?: boolean): boolean => {
    // Firestore not loaded yet — use static fallback
    if (map === null) {
      return !!staticSoldOut;
    }
    // Firestore has this item — use it as source of truth
    if (id in map) {
      return map[id];
    }
    // Item not in Firestore — use static fallback
    return !!staticSoldOut;
  };

  return { isSoldOut, loaded };
}
